import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */

class QuantitySelectorComponent extends Component {
  /**
   * Called when the element is connected to the document's DOM.
   */
  connectedCallback() {
    super.connectedCallback();
    this.updateButtonStates();
  }


  /**
   * Updates the disabled state of minus/plus buttons.
   */
  updateButtonStates() {
    const input = this.refs.quantityInput;
    const buttons = this.refs.quantityButtons;
    if (!input || !buttons) return;

    const current = parseFloat(input.value) || 0;
    const min = parseFloat(input.min) || 0;
    const max = parseFloat(input.max) || Infinity;


    const btns = Array.isArray(buttons) ? buttons : [buttons];

    btns.forEach((btn) => {
      if (!(btn instanceof HTMLButtonElement)) return;
      if (btn.name === 'minus') {
        btn.disabled = current <= min;
      } else if (btn.name === 'plus') {
        btn.disabled = current >= max;
      }
    });
  }



  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    const input = this.refs.quantityInput;
    const step = parseFloat(input.step) || 1;
    const current = parseFloat(input.value) || 0;
    input.value = String(current + step);
    this.#onQuantityChange(event);
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    const input = this.refs.quantityInput;
    const step = parseFloat(input.step) || 1;
    const min = parseFloat(input.min) || 0;
    const current = parseFloat(input.value) || 0;
    const next = current - step;
    // Never go below min (or below 0 if no min set)
    input.value = String(Math.max(next, min));
    this.#onQuantityChange(event);
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange(event);
  }

  /**
   * Handles the quantity change event.
   * @param {Event} event - The event.
   */
  #onQuantityChange(event) {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    this.updateButtonStates();
    const newValue = parseInt(quantityInput.value);

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const numValue = parseFloat(quantityInput.value);
    const numMin = parseFloat(quantityInput.min);
    const numMax = parseFloat(quantityInput.max);

    const container = quantityInput.closest("tr, .inner-grid, .product-form-buttons, .product-information, .quick-add-modal__content, .carts_items");
    const hasMultiplier = container ? container.querySelector(".quntity-note[data-multiplier]") : null;

    if (!isNaN(numMin) && numValue < numMin && !hasMultiplier) quantityInput.value = String(numMin);
    if (!isNaN(numMax) && numValue > numMax) quantityInput.value = String(numMax);
  };


  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
